import React, { useEffect, useMemo, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { addDestinationToTracker } from "helpers/destinations/createDestination";

import { GenericModal } from "components/GenericModal";
import { Step } from "components/organisms/CreateSourceModal/CreateSourceModal.types";
import {
    CatalogContent,
    CatalogControls,
    CatalogGrid,
    ConfigureIntro,
    creationConfigureContentSx,
    creationContentSx,
    CreationFooter,
    creationFooterSx,
    creationHeaderSx,
    creationPaperSx,
    FormCard,
    PaddedContent,
    StepIndicator,
    useTypeCatalog,
} from "components/organisms/CreationModal";
import {
    buildDestinationCatalogItems,
    DestinationCreationForm,
    DestinationFormFooter,
    useDestinationCreation,
} from "components/organisms/DestinationCreation";
import { WIZARD_MODAL_CONTEXT } from "components/organisms/SignalWizard/SignalWizard.constants";
import {
    TrackerStepContent,
    TrackerStepFooter,
    useTrackerStep,
} from "components/organisms/TrackerStep";
import SwalAlertComp from "components/SwalAlert";

import { getAllDestinationsRedux } from "../../../redux/slices/destinations/destinations.asyncActions";
import { closeModal } from "../../../redux/slices/modal/modal.slice";
import { getSingleTrackerRedux } from "../../../redux/slices/trackers/trackers.asyncActions";
import { setPreSelectedDestinationIds } from "../../../redux/slices/wizardSourceDestination/wizardSourceDestination.slice";

/**
 * Modal host for the shared destination creation flow. Owns only modal
 * concerns: the type catalog step, closing, attaching created destinations
 * to the tracker, the "attach to tracker" step and the redirect to the wizard.
 */
const CreateDestinationModal = ({
    data,
    trackers,
    singleTracker,
    singleTrackerStatus,
    language,
    destinationsTypes,
    sourcesTypes,
    bussiness,
    appObject,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const trackerId = data?.tracker_id;
    const isWizardContext = data?.context === WIZARD_MODAL_CONTEXT;
    const lockedDestinationType = data?.destinationType;

    const [step, setStep] = useState(
        lockedDestinationType ? Step.INFO : Step.CATALOG,
    );
    const [pendingType, setPendingType] = useState(
        lockedDestinationType ?? null,
    );
    const [createdDestinationIds, setCreatedDestinationIds] = useState([]);

    const refreshDestinations = () => dispatch(getAllDestinationsRedux());

    const getTracker =
        trackerId && (() => dispatch(getSingleTrackerRedux(trackerId)));

    const dismiss = () => dispatch(closeModal());

    const handleClose = () => {
        dismiss();
        (step === Step.TRACKER || !trackers?.length) && refreshDestinations();
    };

    const navigateToWizard = (destinationIds) => {
        dispatch(setPreSelectedDestinationIds(destinationIds));
        handleClose();
        navigate("/wizard");
    };

    const handleDestinationCreated = async (destinationId) => {
        const destinationIds = (
            Array.isArray(destinationId) ? destinationId : [destinationId]
        ).filter((id) => id != null && id !== "");

        if (isWizardContext) {
            dispatch(setPreSelectedDestinationIds(destinationIds));
            refreshDestinations();
            dismiss();
            return;
        }

        if (trackerId) {
            const response = await addDestinationToTracker(
                { destinations: destinationIds },
                trackerId,
            );
            handleClose();
            if (response.status) {
                getTracker();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message && response.message,
                    icon: "error",
                });
            }
            return;
        }

        setCreatedDestinationIds(destinationIds);
        if (trackers?.length) {
            setStep(Step.TRACKER);
        } else {
            navigateToWizard(destinationIds);
        }
    };

    const destinationCreation = useDestinationCreation({
        language,
        destinationsTypes,
        sourcesTypes,
        tracker: trackerId ? singleTracker : undefined,
        bussiness,
        appObject,
        isTrackerLoading: singleTrackerStatus === "loading",
        sourceTypeCodes: isWizardContext ? data?.sourceTypeCodes : undefined,
        initialDestinationType: lockedDestinationType,
        isTypeLocked: Boolean(lockedDestinationType),
        onCreated: handleDestinationCreated,
        onCancel: handleClose,
        hostActions: {
            trackerId,
            getTracker,
            refresh: () => (trackerId ? getTracker() : refreshDestinations()),
            refreshDestinations,
            dismiss,
        },
    });

    const {
        form,
        availableDestinationCodes,
        isTypeSelectLoading,
        isTypeLocked,
        isAnyMappingOpen,
        handleTypeChange,
        resetFormState,
    } = destinationCreation;

    // Catalog step
    const catalogItems = useMemo(
        () =>
            buildDestinationCatalogItems(
                destinationsTypes,
                availableDestinationCodes,
                language,
            ),
        [destinationsTypes, availableDestinationCodes, language],
    );
    const catalog = useTypeCatalog(catalogItems);

    const selectedItem = useMemo(
        () =>
            catalogItems.find(
                (item) => item.code === (form.destinationCode ?? pendingType),
            ),
        [catalogItems, form.destinationCode, pendingType],
    );

    const continueToConfigure = (code = pendingType) => {
        if (!code) return;
        setPendingType(code);
        handleTypeChange(null, { value: code });
        setStep(Step.INFO);
    };

    const backToCatalog = () => {
        resetFormState();
        setStep(Step.CATALOG);
    };

    const canGoBack = !isTypeLocked;

    const trackerStep = useTrackerStep({
        destinationId: createdDestinationIds,
        language,
        handleClose,
        handleGetDestinations: refreshDestinations,
    });

    const stepLabels = [
        language?.destination_modal?.choose_destination ?? "Choose destination",
        language?.destination_modal?.configure ?? "Configure",
    ];
    const createTitle =
        language?.destination_modal?.create_destination ?? "Create destination";
    const createSubtitle =
        language?.destination_modal?.choose_destination_subtitle ??
        "Choose where your signals should be sent.";

    const renderHeaderProps = () => {
        if (step === Step.CATALOG) {
            return {
                title: createTitle,
                subtitle: createSubtitle,
                headerCenter: (
                    <StepIndicator steps={stepLabels} activeIndex={0} />
                ),
                headerBottom: (
                    <CatalogControls
                        catalog={catalog}
                        allLabel={
                            language?.destination_modal?.all_destinations ??
                            "All destinations"
                        }
                        searchPlaceholder={
                            language?.destination_modal?.search_destinations ??
                            "Search destinations…"
                        }
                    />
                ),
            };
        }
        if (step === Step.INFO && !isAnyMappingOpen) {
            return {
                title: createTitle,
                subtitle: createSubtitle,
                headerCenter: (
                    <StepIndicator
                        steps={stepLabels}
                        activeIndex={1}
                        onStepClick={canGoBack ? backToCatalog : undefined}
                    />
                ),
            };
        }
        return { title: createTitle, subtitle: createSubtitle };
    };

    const renderContent = () => {
        if (step === Step.CATALOG) {
            return (
                <CatalogContent>
                    <CatalogGrid
                        catalog={catalog}
                        value={pendingType}
                        onChange={setPendingType}
                        onConfirm={continueToConfigure}
                        isLoading={isTypeSelectLoading}
                        emptyTitle={
                            language?.destination_modal?.no_catalog_results ??
                            "Nothing matches your search."
                        }
                    />
                </CatalogContent>
            );
        }
        if (step === Step.INFO) {
            if (isAnyMappingOpen) {
                return (
                    <PaddedContent>
                        <DestinationCreationForm
                            destinationCreation={destinationCreation}
                        />
                    </PaddedContent>
                );
            }
            const typeName = selectedItem?.name ?? "";
            return (
                <PaddedContent>
                    <ConfigureIntro
                        image={selectedItem?.image}
                        name={typeName}
                        title={`${language?.destination_modal?.configure ?? "Configure"} ${typeName}`.trim()}
                        description={selectedItem?.description}
                    />
                    <FormCard>
                        <DestinationCreationForm
                            destinationCreation={destinationCreation}
                        />
                    </FormCard>
                </PaddedContent>
            );
        }
        return (
            <TrackerStepContent trackers={trackers} trackerStep={trackerStep} />
        );
    };

    const renderFooter = () => {
        const statusLabel =
            selectedItem &&
            `${selectedItem.name} ${
                language?.destination_modal?.selected ?? "selected"
            }`;

        if (step === Step.CATALOG) {
            return (
                <CreationFooter
                    onCancel={handleClose}
                    cancelLabel={language?.buttons?.cancel ?? "Cancel"}
                    statusLabel={statusLabel}
                    onSubmit={() => continueToConfigure()}
                    submitLabel={language?.buttons?.continue ?? "Continue"}
                    submitEndIcon={<ArrowForwardIcon />}
                    isSubmitDisabled={!pendingType}
                    submitTestId="create-destination-continue"
                />
            );
        }
        if (step === Step.INFO) {
            return (
                <DestinationFormFooter
                    language={language}
                    destinationCode={form.destinationCode}
                    footerAction={destinationCreation.footerAction}
                    onBack={
                        canGoBack && !isAnyMappingOpen
                            ? backToCatalog
                            : undefined
                    }
                    onCancel={destinationCreation.handleClose}
                    onSubmit={destinationCreation.handleSave}
                    isSubmitDisabled={destinationCreation.isSaveDisabled}
                    isSubmitLoading={destinationCreation.isFooterLoading}
                    submitButtonRef={destinationCreation.submitButtonRef}
                />
            );
        }
        return (
            <TrackerStepFooter language={language} trackerStep={trackerStep} />
        );
    };

    const footer = renderFooter();

    return (
        <GenericModal
            open
            onClose={destinationCreation.handleClose}
            // GenericModal forwards Escape to `onClose` on top of the Dialog's
            // own Escape handling; mapping close handlers must run only once.
            disableEscapeKeyDown
            maxWidth={false}
            fullWidth
            paperSx={creationPaperSx}
            headerSx={creationHeaderSx}
            contentSx={
                step === Step.INFO && !isAnyMappingOpen
                    ? creationConfigureContentSx
                    : creationContentSx
            }
            footerSx={creationFooterSx}
            scrollToTopKey={`${step}:${isAnyMappingOpen}`}
            {...renderHeaderProps()}
            content={renderContent()}
            footer={footer || undefined}
        />
    );
};

export default CreateDestinationModal;
